import { getConfig } from '@/shared/config'
import { getAccessToken } from '@/shared/api'
import {
  WS_CLOSE,
  WS_LIMITS,
  wsEnvelopeSchema,
  type WsClient,
  type WsClientOptions,
  type WsEnvelope,
  type WsHandler,
  type WsStatus,
} from './types'

const HEARTBEAT_INTERVAL_MS = 25_000
const PONG_TIMEOUT_MS = 10_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

/** Числовые состояния сокета — без обращения к глобальному `WebSocket` (его нет в jsdom/SSR). */
const SOCKET_CONNECTING = 0
const SOCKET_OPEN = 1

/**
 * Тикет вместо заголовка: браузер не умеет слать Authorization в WS-handshake. Ручка
 * `POST /v1/ws/ticket` (одноразовый тикет на 30 с, частота 60/60 с). Голый `fetch` допустим
 * только в `shared/ws`/`shared/api`/`shared/config` (правило 3).
 */
async function fetchTicket(): Promise<string | null> {
  try {
    const response = await fetch(`${getConfig().apiBaseUrl}/ws/ticket`, {
      method: 'POST',
      credentials: 'include',
      headers: getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {},
    })
    if (!response.ok) return null

    const data = (await response.json()) as { ticket?: string }
    return data.ticket ?? null
  } catch {
    return null
  }
}

/**
 * Один сокет на приложение. Фичи только подписываются на топики.
 * Реконнект, heartbeat и восстановление подписок — забота клиента, а не вызывающего кода.
 */
export function createWsClient(options: WsClientOptions = {}): WsClient {
  const createSocket = options.createSocket ?? ((url: string) => new WebSocket(url))
  const getTicket = options.getTicket ?? fetchTicket
  const getUrl = options.getUrl ?? (() => getConfig().wsUrl)

  let socket: WebSocket | null = null
  let status: WsStatus = 'idle'
  let attempt = 0
  let manuallyClosed = false

  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let pongTimer: ReturnType<typeof setTimeout> | null = null

  const handlers = new Map<string, Set<WsHandler>>()
  const statusListeners = new Set<(status: WsStatus) => void>()
  const closeListeners = new Set<(code: number) => void>()
  const eventListeners = new Set<WsHandler>()
  const outbox: string[] = []

  function setStatus(next: WsStatus): void {
    status = next
    statusListeners.forEach((listener) => listener(next))
  }

  function clearTimers(): void {
    if (heartbeatTimer) clearInterval(heartbeatTimer)
    if (pongTimer) clearTimeout(pongTimer)
    if (reconnectTimer) clearTimeout(reconnectTimer)
    heartbeatTimer = null
    pongTimer = null
    reconnectTimer = null
  }

  function scheduleReconnect(): void {
    if (manuallyClosed || reconnectTimer) return

    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
    const jitter = Math.random() * 0.3 * delay
    attempt += 1

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, delay + jitter)
  }

  function startHeartbeat(): void {
    heartbeatTimer = setInterval(() => {
      if (socket?.readyState !== SOCKET_OPEN) return

      socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }))
      // В WebView сокет умирает молча: сервер рвёт при молчании >70 с, а мы — если нет pong.
      if (pongTimer) clearTimeout(pongTimer)
      pongTimer = setTimeout(() => socket?.close(), PONG_TIMEOUT_MS)
    }, HEARTBEAT_INTERVAL_MS)
  }

  function handleMessage(event: MessageEvent<string>): void {
    let parsed: unknown
    try {
      parsed = JSON.parse(event.data)
    } catch {
      console.warn('[ws] сообщение не является JSON')
      return
    }

    const result = wsEnvelopeSchema.safeParse(parsed)
    if (!result.success) {
      console.warn('[ws] неизвестный формат сообщения', result.error.issues)
      return
    }

    const envelope = result.data
    if (envelope.type === 'pong') {
      if (pongTimer) clearTimeout(pongTimer)
      pongTimer = null
      return
    }

    // Конверт ошибки: topic.unknown — живём дальше; topic.forbidden сервер закроет кодом 4403.
    if (envelope.type === 'error') {
      console.warn('[ws] сервер сообщил об ошибке', envelope.payload)
      return
    }

    if (envelope.topic) {
      handlers.get(envelope.topic)?.forEach((handler) => handler(envelope))
      return
    }

    // Событие уровня соединения без топика (session.revoked): реагирует app-слой.
    eventListeners.forEach((listener) => listener(envelope))
  }

  function connect(): void {
    if (
      socket &&
      (socket.readyState === SOCKET_OPEN || socket.readyState === SOCKET_CONNECTING)
    ) {
      return
    }

    manuallyClosed = false
    setStatus('connecting')

    void getTicket().then((ticket) => {
      if (manuallyClosed) return

      const base = getUrl()
      const url = ticket ? `${base}?ticket=${encodeURIComponent(ticket)}` : base

      socket = createSocket(url)

      socket.onopen = () => {
        attempt = 0
        setStatus('open')
        startHeartbeat()

        // Восстанавливаем подписки и досылаем накопленное.
        handlers.forEach((_set, topic) =>
          socket?.send(JSON.stringify({ type: 'subscribe', topic })),
        )
        while (outbox.length > 0) {
          const message = outbox.shift()
          if (message) socket?.send(message)
        }
      }

      socket.onmessage = handleMessage

      socket.onclose = (event: CloseEvent) => {
        clearTimers()
        setStatus('closed')

        const code = event.code
        closeListeners.forEach((listener) => listener(code))

        // 4401/4403 — переподключаться нечем: сеанс/доступ недействителен (реагирует app-слой).
        // 4409 (смена юрлица) и обрыв связи — переподключаемся по обычному расписанию.
        if (code === WS_CLOSE.unauthorized || code === WS_CLOSE.forbidden) {
          manuallyClosed = true
          return
        }
        scheduleReconnect()
      }

      socket.onerror = () => socket?.close()
    })
  }

  function disconnect(): void {
    manuallyClosed = true
    clearTimers()
    socket?.close()
    socket = null
    setStatus('idle')
  }

  function subscribe(topic: string, handler: WsHandler): () => void {
    const set = handlers.get(topic) ?? new Set<WsHandler>()
    const fresh = !handlers.has(topic)
    set.add(handler)
    handlers.set(topic, set)

    if (fresh && handlers.size > WS_LIMITS.maxTopics) {
      console.warn('[ws] превышен предел топиков', handlers.size)
    }

    // Подписку шлём один раз на топик: при нескольких обработчиках сервер не задваивает.
    if (fresh && socket?.readyState === SOCKET_OPEN) {
      socket.send(JSON.stringify({ type: 'subscribe', topic }))
    }

    return () => {
      const current = handlers.get(topic)
      current?.delete(handler)

      if (current && current.size === 0) {
        handlers.delete(topic)
        if (socket?.readyState === SOCKET_OPEN) {
          socket.send(JSON.stringify({ type: 'unsubscribe', topic }))
        }
      }
    }
  }

  function send(type: string, payload?: unknown, topic?: string): void {
    const message = JSON.stringify({ type, payload, topic, ts: Date.now() })

    if (message.length > WS_LIMITS.maxMessageBytes) {
      console.warn('[ws] сообщение больше предела, отброшено', type)
      return
    }

    if (socket?.readyState === SOCKET_OPEN) {
      socket.send(message)
      return
    }
    // Соединения нет — отправим после реконнекта.
    outbox.push(message)
  }

  function onStatusChange(listener: (status: WsStatus) => void): () => void {
    statusListeners.add(listener)
    return () => statusListeners.delete(listener)
  }

  function onClose(listener: (code: number) => void): () => void {
    closeListeners.add(listener)
    return () => closeListeners.delete(listener)
  }

  function onEvent(listener: (envelope: WsEnvelope) => void): () => void {
    eventListeners.add(listener)
    return () => eventListeners.delete(listener)
  }

  // Нудж реконнекта, только если соединение было и оборвалось (`closed`). В `idle` клиент не
  // поднимаем: нет сеанса (вход, стенд) либо это намеренный `disconnect` — connect зовёт провайдер.
  const nudgeReconnect = () => {
    if (status === 'closed' && !manuallyClosed) connect()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', nudgeReconnect)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') nudgeReconnect()
    })
  }

  return { connect, disconnect, subscribe, send, getStatus: () => status, onStatusChange, onClose, onEvent }
}

export const wsClient = createWsClient()
